import React, { useState, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import toast from "react-hot-toast";
import { ScenarioDeploy } from "@/model";
import { deployVersion } from "@/util/stringUtil";
import { useScenarioId, useModal } from "@/hook";
import { useDeployScenario, usePaginatedScenarioDeploy, useRestoreScenarioDeploy } from "@/query/deployment";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { DropdownButton, DropdownItem, DropdownItems, DropdownGroup, Textarea } from "@/component/input";


export const DEPLOY_MODAL_ID = "deploy-modal-id";

const DeployModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();

    // const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const [deployType, setDeployType] = useState<"next" | "previous">("next");
    const [oldDeploy, setOldDeploy] = useState<ScenarioDeploy>();
    const [description, setDescription] = useState<string>("");

    const { mutateAsync: deploy, isPending: isDeploying } = useDeployScenario(scenarioId);
    const { mutateAsync: restore, isPending: isRestoring } = useRestoreScenarioDeploy(scenarioId);
    const { data: scenarioDeployPage } = usePaginatedScenarioDeploy(scenarioId, 0, 50);

    const onClickCancel = () => {
        modal.close(DEPLOY_MODAL_ID);
    };

    const onClickConfirm = async () => {
        if (deployType === "next") {
            await deploy({
                version: nextVersion,
                description: description
            }).then(() => {
                toast.success(intl.formatMessage({ id: "i06030" }));
                modal.close(DEPLOY_MODAL_ID);
            }).catch((error) => {
                if (error.response.status === 422) {
                    toast.error(intl.formatMessage({ id: "i06029" }));
                } else {
                    toast.error(intl.formatMessage({ id: "i06031" }));
                }
            });
        } else {
            if (!oldDeploy) {
                return;
            }
            await restore({
                deployId: oldDeploy.id,
                description: description
            }).then(() => {
                toast.success(intl.formatMessage({ id: "i06030" }));
                modal.close(DEPLOY_MODAL_ID);
            })
            .catch((error) => {
                if (error.response.status === 422) {
                    toast.error(intl.formatMessage({ id: "i06028" }));
                } else {
                    toast.error(intl.formatMessage({ id: "i06031" }));
                }
            });
        }
    };

    const nextVersion = useMemo(() => {
        if (!scenarioDeployPage?.content.length) {
            return "1";
        }
        const latestVersion = parseInt(scenarioDeployPage.content[0].version);
        return ((latestVersion || 0) + 1).toString();
    }, [scenarioDeployPage]);

    useEffect(() => {
        if (deployType === "previous" && !oldDeploy) {
            setOldDeploy(scenarioDeployPage?.content[0]);
        } else if (deployType === "next") {
            setOldDeploy(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployType]);

    useEffect(() => {
        setDescription(oldDeploy?.description ?? "");
    }, [oldDeploy]);

    return (
        <Modal id={DEPLOY_MODAL_ID}
               size="md"
               title={intl.formatMessage({ id: "i06002" })}
               onClose={() => {
                   setDeployType("next");
                   setOldDeploy(undefined);
                   setDescription("");
               }}
        >
            <div className="modal-body-8">
                <div className="flex flex-col gap-6">
                    <fieldset className="w-full flex">
                        <legend className="sr-only">
                            {intl.formatMessage({ id: "i06002" })}
                        </legend>
                        <div className="w-1/2">
                            <label className="flex gap-1 leading-none cursor-pointer">
                                <input type="radio"
                                       name="select-deploy-type"
                                       className="input-radio-chat42 size-4"
                                       checked={deployType === "next"}
                                       onChange={() => setDeployType("next")}
                                />
                                <div className="text-[13px] text-black-500">
                                    {intl.formatMessage({ id: "i06004" })}
                                </div>
                            </label>
                        </div>
                        <div className="w-1/2">
                            <label className="flex gap-1 leading-none cursor-pointer">
                                <input type="radio"
                                       name="select-deploy-type"
                                       className="input-radio-chat42 size-4"
                                       checked={deployType === "previous"}
                                       onChange={() => setDeployType("previous")}
                                       disabled={!scenarioDeployPage?.content.length}
                                />
                                <div className="text-[13px] text-black-500">
                                    {intl.formatMessage({ id: "i06005" })}
                                </div>
                            </label>
                        </div>
                    </fieldset>

                    <div className="flex flex-col gap-1.5">
                        <div id="deploy-version-label"
                             className="field-label"
                        >
                            {intl.formatMessage({ id: "i06003" })}
                        </div>

                        <DropdownGroup aria-labelledby="deploy-version-label">
                            <DropdownButton invalid={deployType === "previous" && !scenarioDeployPage?.content.length}
                                            disabled={deployType === "next"}
                            >
                                {deployType === "next" && deployVersion(nextVersion)}
                                {deployType === "previous" && (
                                    oldDeploy ? deployVersion(oldDeploy.version) : intl.formatMessage({ id: "i06006" })
                                )}
                            </DropdownButton>
                            <DropdownItems maxHeight={120}>
                                {scenarioDeployPage?.content.map((deploy, index) => (
                                    <DropdownItem key={deploy.id}
                                                isOdd={index % 2 === 1}
                                                onClick={() => setOldDeploy(deploy)}
                                    >
                                        {deployVersion(deploy.version)}
                                    </DropdownItem>
                                ))}
                            </DropdownItems>
                        </DropdownGroup>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div className="field-label"
                             id="deploy-description-label"
                        >
                            {intl.formatMessage({ id: "i06013" })}
                        </div>
                        <Textarea aria-labelledby="deploy-description-label"
                                  value={description}
                                  className="modal-input"
                                  placeholder={intl.formatMessage({ id: "i06014" })}
                                  onChange={setDescription}
                        />
                    </div>
                </div>
                <div className="modal-btn-group mt-7.5">
                    <Button variant="secondary"
                            size="sm"
                            onClick={onClickCancel}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button variant="primary"
                            size="sm"
                            disabled={isDeploying || isRestoring}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DeployModal;
